package com.karyadina.service

import com.karyadina.model.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.Image
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine
import kotlin.math.min
import kotlin.math.sqrt

actual class ImageResizerServiceImpl : ImageResizerService {
    
    actual override suspend fun resizeImage(
        image: ImageItem,
        config: ImageResizeConfig
    ): ImageResizeResult = suspendCoroutine { continuation ->
        try {
            val img = Image()
            
            img.onload = {
                try {
                    val originalWidth = img.width
                    val originalHeight = img.height
                    
                    // Calculate target dimensions
                    // Calculate source and target dimensions
                    val crop = config.cropRect
                    val sourceX = (crop?.left ?: 0f) * originalWidth
                    val sourceY = (crop?.top ?: 0f) * originalHeight
                    val sourceWidth = (crop?.right ?: 1f) * originalWidth - sourceX
                    val sourceHeight = (crop?.bottom ?: 1f) * originalHeight - sourceY
                    
                    val (targetWidth, targetHeight) = calculateDimensions(
                        sourceWidth.toInt(),
                        sourceHeight.toInt(),
                        config.targetWidth,
                        config.targetHeight,
                        config.maintainAspectRatio
                    )
                    
                    // Create canvas and draw resized image
                    val canvas = document.createElement("canvas") as HTMLCanvasElement
                    canvas.width = targetWidth
                    canvas.height = targetHeight
                    
                    val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
                    
                    // Use 9-argument drawImage for cropping: source image, source rect, dest rect
                    ctx.drawImage(
                        img, 
                        sourceX.toDouble(), sourceY.toDouble(), sourceWidth.toDouble(), sourceHeight.toDouble(),
                        0.0, 0.0, targetWidth.toDouble(), targetHeight.toDouble()
                    )
                    
                    // Convert to blob with quality
                    val quality = config.quality / 100.0
                    val mimeType = when (config.format) {
                        ImageFormat.PNG -> "image/png"
                        ImageFormat.WEBP -> "image/webp"
                        else -> "image/jpeg"
                    }
                    
                    canvas.toBlob({ blob ->
                        if (blob != null) {
                            val resizedUri = js("URL.createObjectURL(blob)") as String
                            
                            continuation.resume(
                                ImageResizeResult(
                                    success = true,
                                    originalImage = image,
                                    resizedUri = resizedUri,
                                    resizedSizeInBytes = blob.size.toLong(),
                                    resizedWidth = targetWidth,
                                    resizedHeight = targetHeight,
                                    compressionRatio = blob.size.toDouble() / image.sizeInBytes
                                )
                            )
                        } else {
                            continuation.resume(
                                ImageResizeResult(
                                    success = false,
                                    originalImage = image,
                                    error = "Failed to create blob from canvas"
                                )
                            )
                        }
                    }, mimeType, quality)
                    
                } catch (e: Exception) {
                    continuation.resume(
                        ImageResizeResult(
                            success = false,
                            originalImage = image,
                            error = "Failed to resize: ${e.message}"
                        )
                    )
                }
            }
            
            img.onerror = { _, _, _, _, _ ->
                continuation.resume(
                    ImageResizeResult(
                        success = false,
                        originalImage = image,
                        error = "Failed to load image"
                    )
                )
            }
            
            // Trigger image load
            img.src = image.uri
            
        } catch (e: Exception) {
            continuation.resume(
                ImageResizeResult(
                    success = false,
                    originalImage = image,
                    error = "Resize failed: ${e.message}"
                )
            )
        }
    }
    
    actual override suspend fun resizeImages(
        images: List<ImageItem>,
        config: ImageResizeConfig
    ): List<ImageResizeResult> {
        return images.map { image ->
            resizeImage(image, config)
        }
    }
    
    actual override fun applyPreset(preset: ResizePreset): ImageResizeConfig {
        return ImageResizeConfig(
            targetSizeKB = preset.targetSizeKB,
            targetWidth = preset.targetWidth,
            targetHeight = preset.targetHeight,
            quality = 85,
            maintainAspectRatio = true,
            format = ImageFormat.JPEG
        )
    }
    
    private fun calculateDimensions(
        currentWidth: Int,
        currentHeight: Int,
        targetWidth: Int?,
        targetHeight: Int?,
        maintainAspectRatio: Boolean
    ): Pair<Int, Int> {
        if (!maintainAspectRatio && targetWidth != null && targetHeight != null) {
            return Pair(targetWidth, targetHeight)
        }
        
        val aspectRatio = currentWidth.toDouble() / currentHeight.toDouble()
        
        return when {
            targetWidth != null && targetHeight != null -> {
                // Both specified, maintain aspect ratio by choosing smaller scale
                val widthScale = targetWidth.toDouble() / currentWidth
                val heightScale = targetHeight.toDouble() / currentHeight
                val scale = min(widthScale, heightScale)
                Pair(
                    (currentWidth * scale).toInt(),
                    (currentHeight * scale).toInt()
                )
            }
            targetWidth != null -> {
                // Only width specified
                Pair(targetWidth, (targetWidth / aspectRatio).toInt())
            }
            targetHeight != null -> {
                // Only height specified
                Pair((targetHeight * aspectRatio).toInt(), targetHeight)
            }
            else -> {
                // No dimensions specified, return original
                Pair(currentWidth, currentHeight)
            }
        }
    }
}
